import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { ToggleButton } from "primereact/togglebutton";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useOutletContext } from "react-router-dom";
import NumberUtil from "../../../utility/NumberUtil";
import { queryCarbonFootprintChartData } from "../../../utility/service/carbonFootprint-service";
import { checkDpSuccess } from "../../../utility/service/tool-service";
import CusDountChart from "../../dashboard/CusDountChart";

const CarbonFootprintDashboard = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [lifeCycleData, setLifeCycleData] = useState({});
  const [allLifeCycleData, setAllLifeCycleData] = useState([]);
  const [lifecycleCheck, setlifeCycleCheck] = useState(false);
  const { companyName } = useOutletContext();

  useEffect(() => {
    if (lifeCycleData) {
      setAllLifeCycleData(mapLifeCycleData(lifeCycleData, location?.state?.itemObj?.productType));
    }
  }, [lifeCycleData, lifecycleCheck]);

  useEffect(() => {
    if (JSON.stringify(location?.state?.itemObj) !== "{}") {
      let queryObj = location?.state?.itemObj;
      queryCarbonFootprintChartData({ carbonFpId: queryObj?.carbonFpId, productType: queryObj?.productType }).then(
        (res) => {
          if (checkDpSuccess(res)) {
            let data = res?.data?.RespBody;
            setLifeCycleData(data);
          }
        }
      );
    }
  }, [location]);

  const mapLifeCycleData = (cycle, productType) => {
    const rawMaterialData = (cycle?.rawMaterialData || []).map((item) => ({
      label: t("stage.rawMaterial"),
      value: parseFloat(item.dataPercent),
      name: item.dataName,
      cfValue: item.cfValue,
    }));
    const manufacturingData = (cycle?.manufacturingData || []).map((item) => ({
      label: t("stage.manufacturing"),
      value: parseFloat(item.dataPercent),
      name: item.dataName,
      cfValue: item.cfValue,
    }));

    const distributionData = (cycle?.distributionData || []).map((item) => ({
      label: t("stage.distribution"),
      value: parseFloat(item.dataPercent),
      name: item.dataName,
      cfValue: item.cfValue,
    }));

    const useData = (cycle?.useData || []).map((item) => ({
      label: t("stage.use"),
      value: parseFloat(item.dataPercent),
      name: item.dataName,
      cfValue: item.cfValue,
    }));

    const serviceData = (cycle?.serviceData || []).map((item) => ({
      label: t("stage.service"),
      value: parseFloat(item.dataPercent),
      name: item.dataName,
      cfValue: item.cfValue,
    }));

    const disposalData = (cycle?.disposalData || []).map((item) => ({
      label: t("stage.disposal"),
      value: parseFloat(item.dataPercent),
      name: item.dataName,
      cfValue: item.cfValue,
    }));
    if (productType === "G") {
      const combinedData = [...rawMaterialData, ...manufacturingData, ...distributionData, ...useData, ...disposalData];
      if (lifecycleCheck) {
        return combinedData;
      } else {
        return [
          {
            value: Number(cycle?.rawMaterialPercent),
            name: t("stage.rawMaterial"),
            unit: "%",
            key: "rawMaterial",
            detail: cycle?.rawMaterialData,
          },
          {
            value: Number(cycle?.manufacturingPercent),
            name: t("stage.manufacturing"),
            unit: "%",
            key: "manufacturing",
          },
          { value: Number(cycle?.distributionPercent), name: t("stage.distribution"), unit: "%", key: "distribution" },
          { value: Number(cycle?.usePercent), name: t("stage.use"), unit: "%", key: "use" },
          { value: Number(cycle?.disposalPercent), name: t("stage.disposal"), unit: "%", key: "disposal" },
        ];
      }
    } else if (productType === "S") {
      if (lifecycleCheck) {
        return [...rawMaterialData, ...serviceData, ...disposalData];
      } else {
        return [
          { value: Number(cycle?.rawMaterialPercent), name: t("stage.rawMaterial"), unit: "%", key: "rawMaterial" },
          { value: Number(cycle?.servicePercent), name: t("stage.service"), unit: "%", key: "service" },
          { value: Number(cycle?.disposalPercent), name: t("stage.disposal"), unit: "%", key: "disposal" },
        ];
      }
    }
  };

  const lifeCycleForm = (data, productType = "G") => {
    let lifeArrayData = [];
    if (productType === "G") {
      lifeArrayData = [
        { name: t("stage.rawMaterial"), value: "rawMaterial", percent: "rawMaterialPercent" },
        { name: t("stage.manufacturing"), value: "manufacturing", percent: "manufacturingPercent" },
        { name: t("stage.distribution"), value: "distribution", percent: "distributionPercent" },
        { name: t("stage.use"), value: "use", percent: "usePercent" },
        { name: t("stage.disposal"), value: "disposal", percent: "disposalPercent" },
      ];
    } else if (productType === "S") {
      lifeArrayData = [
        { name: t("stage.rawMaterial"), value: "rawMaterial", percent: "rawMaterialPercent" },
        { name: t("stage.service"), value: "service", percent: "servicePercent" },
        { name: t("stage.disposal"), value: "disposal", percent: "disposalPercent" },
      ];
    }

    return (
      <div className="dashboard-grid">
        <div className="p-card">
          <div className="p-card-body">
            <div className="p-card-title">
              <span>{t("emissionsStatisticsChart")}</span>
              <ToggleButton
                className="w-10rem"
                onLabel={t("SummaryGraph")}
                offLabel={t("stageChart")}
                onIcon="pi pi-chart-pie"
                offIcon="pi pi-table"
                tooltip={t("dashboard.change")}
                tooltipOptions={{ position: "top" }}
                checked={lifecycleCheck}
                onChange={(e) => setlifeCycleCheck(e.value)}
              />
            </div>
            <div className="dashboard-table-wrapper">
              {allLifeCycleData?.length !== 0 && allLifeCycleData?.length !== 0 && (
                <CusDountChart
                  isShowLegend={!lifecycleCheck}
                  tooltipFormatter={
                    lifecycleCheck
                      ? {
                          trigger: "item",
                          position: "inside",
                          formatter: (params) => {
                            return `${params.marker} ${params?.data?.label}<br/>${
                              params?.data?.name
                            } : ${NumberUtil.formatWithCommas(params?.data?.cfValue)} (${NumberUtil.formatWithCommas(
                              params?.data?.value
                            )}%)`;
                          },
                        }
                      : {
                          trigger: "item",
                          position: "inside",
                          formatter: (params) => {
                            return `${params.marker} ${params?.data?.name}: ${NumberUtil.formatWithCommas(
                              params?.data?.value
                            )}%`;
                          },
                        }
                  }
                  donutChartData={allLifeCycleData}
                ></CusDountChart>
              )}
            </div>
          </div>
        </div>
        <div className="p-card">
          <div className="p-card-body">
            <div className="p-card-title">
              <span>{t("stageStatisticsTable")}</span>
            </div>
            <div className="dashboard-gas-table-wrapper">
              {lifeArrayData?.map((item, index) => (
                <div key={index} className="dashboard-gas-table">
                  <div className="dashboard-gas-table-header">{item?.name}</div>
                  <div className="dashboard-gas-table-col font-bold">{t("emissionEquivalent")}</div>
                  <div className="dashboard-gas-table-col text-right">
                    {NumberUtil.formatWithCommas(data?.[item?.value])}
                  </div>
                  <div className="dashboard-gas-table-col font-bold">{t("gasProportion")}</div>
                  <div className="dashboard-gas-table-col text-right">
                    {" "}
                    {data?.[item?.percent] ? `${NumberUtil.formatWithCommas(data[item.percent])} %` : "-"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="flex justify-content-between flex-wrap align-items-center">
        <div className="align-items-start">
          <Button
            rounded
            text
            onClick={() => {
              navigate("/management/inventoryCarbonFootprint");
            }}
          >
            <FontAwesomeIcon icon="fa-solid fa-arrow-left-long" />
            {t("button.return_to_list")}
          </Button>
        </div>
        {/* <div className="align-items-end">
            {t('button.dashboard')}
        </div> */}
      </div>
      {/* 上方查詢區   */}
      <div className="p-card bg-gray">
        <div className="p-card-body">
          <div className="grid">
            <div className="col-12">
              <h3 className="m-0">{companyName}</h3>
            </div>
            <div className="col-12 md:col-4">
              <label className="label-has-icon">{t("product_name")}</label>
              <span>{location?.state?.itemObj?.productName}</span>
            </div>
            <div className="col-12 md:col-3">
              <label className="label-has-icon">{t("total_quantity")}</label>
              <span>{NumberUtil.formatWithCommas(location?.state?.itemObj?.quantity)}</span>
            </div>
            <div className="col-12 md:col-5">
              <label className="label-has-icon">{t("inventoryDate")}</label>
              <span>{location?.state?.itemObj?.inspectionDate}</span>
            </div>
          </div>
        </div>
      </div>
      <div className="dashboard-table-wrapper">
        {lifeCycleForm(lifeCycleData, location?.state?.itemObj?.productType)}
      </div>
    </>
  );
};

export default CarbonFootprintDashboard;
